import { get } from 'lodash';
import { useFind } from 'src/api/APIHooks';
import {
  DISPUTED_STATUSES,
  DRAFT_CBO_ADMIN_STATUSES,
  INVOICE_STATUS,
  OPEN_NL_STATUSES,
  OPEN_PAYER_STATUSES,
  REJECTED_CBO_ADMIN_STATUSES,
} from '../constants';
import usePayerWQInteractiveView from './usePayerWQInteractiveView';

const filterOutReadOnlyStatuses = (statuses) => (
  statuses.filter((status) => status !== INVOICE_STATUS.submitted_to_payer)
);

const useInvoiceStatusCount = (
  providerId,
  status,
  isNetworkLead,
  payerId,
  showPayerInvoices,
  disputed = false,
  enabled = true,
) => {
  const defaultFilters = {
    invoice_status: status,
    has_user_archive: false,
    state: 'active',
    under_dispute: disputed,
  };

  const variableFilters = showPayerInvoices ? {
    payer: payerId,
  } :
    {
      ...(isNetworkLead ? { network_lead: providerId } : { provider: providerId }),
    };

  const { isFetching, data } = useFind(
    'invoice',
    {
      ...defaultFilters,
      ...variableFilters,
    },
    {
      page: {
        number: 1,
        size: 1,
      },
      queryConfig: {
        enabled: !!providerId && enabled,
        placeholderData: undefined,
      },
    },
  );

  return isFetching ? undefined : get(data, 'data.paging.total_count');
};

const useInvoiceStatusCounts = (providerId, isNetworkLead, payerId, showPayerInvoices, enabled) => {
  const { payerWQInteractiveView } = usePayerWQInteractiveView(payerId);
  const payerReadOnlyView = !payerWQInteractiveView;
  const submittedToCBOAdmin =
    useInvoiceStatusCount(
      providerId,
      'submitted_contracted_service_note',
      isNetworkLead,
      payerId,
      showPayerInvoices,
      undefined,
      enabled,
    );
  const submittedToNetworkLead = useInvoiceStatusCount(
    providerId,
    'submitted_to_network_lead',
    isNetworkLead,
    payerId,
    showPayerInvoices,
    undefined,
    enabled,
  );
  const draft = useInvoiceStatusCount(
    providerId,
    DRAFT_CBO_ADMIN_STATUSES.join(),
    isNetworkLead,
    payerId,
    showPayerInvoices,
    undefined,
    enabled,
  );

  let variableOpenStatuses;
  if (showPayerInvoices) {
    if (payerReadOnlyView) {
      variableOpenStatuses = OPEN_PAYER_STATUSES;
    } else {
      variableOpenStatuses = filterOutReadOnlyStatuses(OPEN_PAYER_STATUSES);
    }
  } else {
    variableOpenStatuses = OPEN_NL_STATUSES;
  }

  const open = useInvoiceStatusCount(
    providerId,
    variableOpenStatuses.join(','),
    isNetworkLead,
    payerId,
    showPayerInvoices,
    undefined,
    enabled,
  );
  const rejected = useInvoiceStatusCount(
    providerId,
    ['rejected_by_cbo_admin', 'rejected_by_network_lead', 'rejected_by_payer'].join(','),
    isNetworkLead,
    payerId,
    showPayerInvoices,
    undefined,
    enabled,
  );
  const rejectedDWQ = useInvoiceStatusCount(
    providerId,
    REJECTED_CBO_ADMIN_STATUSES.join(','),
    isNetworkLead,
    payerId,
    showPayerInvoices,
    undefined,
    enabled,
  );
  const disputed = useInvoiceStatusCount(
    providerId,
    DISPUTED_STATUSES.join(','),
    isNetworkLead,
    payerId,
    showPayerInvoices,
    true,
    enabled,
  );

  return {
    disputed,
    draft,
    open,
    submittedToCBOAdmin,
    submittedToNetworkLead,
    rejected,
    rejectedDWQ,
  };
};

export default useInvoiceStatusCounts;
